import type { PolicyViewModel } from 'src/api/types/PolicyViewModel';
import type {
  StateLookupOption,
  UpdatePolicyRequest,
  CodeDescriptionLookupOption,
  UpdatePolicyStatePremiumUnifiedRequest,
} from 'src/api/services/policy.service';

import { formatDate } from 'src/sections/employer-data/shared/utils/policyUtils';

import type { EditableStateRow, PolicyEditDraft, PolicyFieldErrors, PolicyInformationDraftDelta } from './policy-information.types';

export const LEGAL_NATURE_OTHER_CODE = '99';

const EMPTY_SUMMARY_VALUE = '[blank]';

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const CODE_FIELD_DISPLAY_LABELS: Record<string, string> = {
  'Legal nature code': 'Legal nature',
  'Retro rating code': 'Retro rating',
  'Minimum premium state code': 'Minimum premium state',
  'Plan type code': 'Plan type',
  'Policy type code': 'Policy type',
};

type DraftDelta = {
  label: string;
  previousValue: string;
  newValue: string;
};

export type ChangedFieldSummaryDisplayOptions = {
  stateLookups?: StateLookupOption[];
  legalNatureLookups?: CodeDescriptionLookupOption[];
  retroRatingLookups?: CodeDescriptionLookupOption[];
  policyTypeLookups?: CodeDescriptionLookupOption[];
  policyPlanTypeLookups?: CodeDescriptionLookupOption[];
  marketLookups?: CodeDescriptionLookupOption[];
};

function isValidUsDate(value: string): boolean {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) {
    return false;
  }

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function compareUsDates(left: string, right: string): number {
  const [leftMonth, leftDay, leftYear] = left.split('/').map(Number);
  const [rightMonth, rightDay, rightYear] = right.split('/').map(Number);
  const leftDate = new Date(leftYear, leftMonth - 1, leftDay).getTime();
  const rightDate = new Date(rightYear, rightMonth - 1, rightDay).getTime();

  return leftDate - rightDate;
}

function isWholeNumberInRange(value: string): boolean {
  if (!/^\d+$/.test(value.trim())) {
    return false;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0 && numberValue <= 9999999999;
}

export function findCodeByDescription(
  lookups: CodeDescriptionLookupOption[],
  description: string | null | undefined
): string {
  if (!description) {
    return '';
  }

  const match = lookups.find((item) => item.description === description);
  return match?.code ?? '';
}

export function findStateCodeByAbbreviation(
  states: StateLookupOption[],
  stateAbbreviation: string | null | undefined
): number {
  if (!stateAbbreviation) {
    return 0;
  }

  const match = states.find((item) => item.stateAbbreviation === stateAbbreviation);
  return match?.stateCode ?? 0;
}

function buildEditableStateRows(viewPolicy: PolicyViewModel): EditableStateRow[] {
  const policyStatePremiums = viewPolicy.policyStatePremiums ?? [];
  const states = [
    ...(viewPolicy.employerStatesOfOperation?.primaryStates ?? []),
    ...(viewPolicy.employerStatesOfOperation?.otherStates ?? []),
  ];

  // policyStatePremiums spans every policy period, so on multi-period/multi-state policies this
  // list can be very large. Bucket by state abbreviation up front instead of re-scanning the full
  // list per row, which was O(states x premiums) and noticeably slow for heavy policy histories.
  const premiumsByState = new Map<string, typeof policyStatePremiums>();
  for (const premium of policyStatePremiums) {
    if ((premium.statePremiumId ?? 0) <= 0) continue;
    const bucket = premiumsByState.get(premium.state ?? '');
    if (bucket) {
      bucket.push(premium);
    } else {
      premiumsByState.set(premium.state ?? '', [premium]);
    }
  }

  const usedIds = new Set<number>();
  let fallbackCursor = 0;

  return states.map((state, index) => {
    const bucket = premiumsByState.get(state.stateAbbreviation ?? '');
    let candidate = bucket?.shift();

    if (!candidate) {
      while (fallbackCursor < policyStatePremiums.length) {
        const next = policyStatePremiums[fallbackCursor];
        fallbackCursor += 1;
        if (!usedIds.has(next.statePremiumId ?? 0)) {
          candidate = next;
          break;
        }
      }
    }

    const statePremiumId = candidate?.statePremiumId ?? 0;
    usedIds.add(statePremiumId);

    return {
      rowKey: `${state.isPrimary ? 'primary' : 'other'}-${statePremiumId || index}`,
      statePremiumId,
      stateCode: Number(state.stateCode ?? 0),
      stateDisplay: state.stateAbbreviation ?? state.stateName ?? '',
      effectiveDate: formatDate(state.effectiveDate) || '',
      expirationDate: formatDate(state.expirationDate) || '',
      premium: String(Math.trunc(state.premium ?? candidate?.estimatedStateStandardPremiumTotal ?? 0)),
    };
  });
}

export function initializeDraft(viewPolicy: PolicyViewModel): PolicyEditDraft {
  const legalNatureCode = String(viewPolicy.legalNatureCode ?? '');
  return {
    interstateRiskNumber: viewPolicy.interstateRiskNumber ?? '',
    legalNatureCode,
    legalNatureOther: legalNatureCode === LEGAL_NATURE_OTHER_CODE ? viewPolicy.legalNatureOther ?? '' : '',
    retroRatingCode: String(viewPolicy.retroRatingCode ?? ''),
    minimumPremiumStateCode: Number(viewPolicy.minimumPremiumStateCode ?? 0),
    planTypeCode: String(viewPolicy.planTypeCode ?? ''),
    producerName: viewPolicy.producerName ?? '',
    policyTypeCode: String(viewPolicy.policyTypeCode ?? ''),
    policyEffectiveDate: formatDate(viewPolicy.policyEffectiveDate) || '',
    policyExpirationDate: formatDate(viewPolicy.policyExpirationDate) || '',
    policyIssueDate: formatDate(viewPolicy.policyIssueDate) || '',
    bodilyInjuryAccidentLimit: String(Math.trunc(viewPolicy.liabilityLimits?.bodilyInjuryAccident ?? 0)),
    bodilyInjuryDiseasePolicyLimit: String(
      Math.trunc(viewPolicy.liabilityLimits?.bodilyInjuryDiseasePolicy ?? 0)
    ),
    bodilyInjuryDiseaseEmployeeLimit: String(
      Math.trunc(viewPolicy.liabilityLimits?.bodilyInjuryDiseaseEmployee ?? 0)
    ),
    employerStatePremiums: buildEditableStateRows(viewPolicy),
  };
}

function validateRequiredFields(draft: PolicyEditDraft, errors: PolicyFieldErrors): void {
  if (!draft.legalNatureCode) errors.legalNatureCode = 'Legal nature is required.';
  if (draft.legalNatureCode === LEGAL_NATURE_OTHER_CODE && !draft.legalNatureOther.trim()) {
    errors.legalNatureOther = 'Legal nature (other) is required.';
  }
  if (!draft.retroRatingCode) errors.retroRatingCode = 'Retro rating is required.';
  if (draft.minimumPremiumStateCode <= 0) errors.minimumPremiumStateCode = 'Minimum premium state is required.';
  if (!draft.planTypeCode) errors.planTypeCode = 'Market is required.';
  if (!draft.producerName.trim()) {
    errors.producerName = 'Agency is required.';
  } else if (draft.producerName.length > 30) {
    errors.producerName = 'Agency must be 30 characters or fewer.';
  }
  if (!draft.policyTypeCode) errors.policyTypeCode = 'Policy type is required.';
}

function validateDates(draft: PolicyEditDraft, errors: PolicyFieldErrors): void {
  const effectiveValid = isValidUsDate(draft.policyEffectiveDate);
  const expirationValid = isValidUsDate(draft.policyExpirationDate);
  if (!effectiveValid) errors.policyEffectiveDate = 'Effective date must use MM/DD/YYYY format.';
  if (!expirationValid) errors.policyExpirationDate = 'Expiration date must use MM/DD/YYYY format.';
  if (!isValidUsDate(draft.policyIssueDate)) errors.policyIssueDate = 'Issue date must use MM/DD/YYYY format.';
  if (effectiveValid && expirationValid && compareUsDates(draft.policyEffectiveDate, draft.policyExpirationDate) > 0) {
    errors.policyEffectiveDate = 'Effective date must be on or before expiration date.';
  }
}

function validateLiabilityLimits(draft: PolicyEditDraft, errors: PolicyFieldErrors): void {
  if (!isWholeNumberInRange(draft.bodilyInjuryAccidentLimit))
    errors.bodilyInjuryAccidentLimit = 'Must be a whole number between 0 and 9999999999.';
  if (!isWholeNumberInRange(draft.bodilyInjuryDiseasePolicyLimit))
    errors.bodilyInjuryDiseasePolicyLimit = 'Must be a whole number between 0 and 9999999999.';
  if (!isWholeNumberInRange(draft.bodilyInjuryDiseaseEmployeeLimit))
    errors.bodilyInjuryDiseaseEmployeeLimit = 'Must be a whole number between 0 and 9999999999.';
}

function validateEmployerStatePremiums(draft: PolicyEditDraft, errors: PolicyFieldErrors): void {
  const rowErrors = (draft.employerStatePremiums ?? []).map((row) => ({
    ...(row.statePremiumId <= 0 || row.stateCode <= 0 ? { stateCode: 'State selection is required.' } : {}),
    ...(!isWholeNumberInRange(row.premium) ? { premium: 'Must be a whole number between 0 and 9999999999.' } : {}),
  }));

  if (rowErrors.some((row) => Object.keys(row).length > 0)) {
    errors.employerStatePremiums = rowErrors;
  }
}

export function hasFieldErrors(errors: PolicyFieldErrors): boolean {
  return Object.values(errors).some(Boolean);
}

const scalarDraftFields: Array<keyof Omit<PolicyEditDraft, 'employerStatePremiums'>> = [
  'interstateRiskNumber',
  'legalNatureCode',
  'legalNatureOther',
  'retroRatingCode',
  'minimumPremiumStateCode',
  'planTypeCode',
  'producerName',
  'policyTypeCode',
  'policyEffectiveDate',
  'policyExpirationDate',
  'policyIssueDate',
  'bodilyInjuryAccidentLimit',
  'bodilyInjuryDiseasePolicyLimit',
  'bodilyInjuryDiseaseEmployeeLimit',
];

export function getPolicyInformationDraftDelta(
  snapshot: PolicyEditDraft,
  draft: PolicyEditDraft
): PolicyInformationDraftDelta {
  const delta: PolicyInformationDraftDelta = {};
  scalarDraftFields.forEach((field) => {
    if (draft[field] !== snapshot[field]) {
      delta[field] = draft[field] as never;
    }
  });

  const rowDelta: NonNullable<PolicyInformationDraftDelta['employerStatePremiums']> = {};
  (draft.employerStatePremiums ?? []).forEach((row) => {
    const previousRow = snapshot.employerStatePremiums?.find(
      (candidate) => candidate.statePremiumId === row.statePremiumId
    );
    if (!previousRow) {
      return;
    }

    const patch: Partial<Pick<EditableStateRow, 'stateCode' | 'stateDisplay' | 'premium'>> = {};
    if (row.stateCode !== previousRow.stateCode) patch.stateCode = row.stateCode;
    if (row.stateDisplay !== previousRow.stateDisplay) patch.stateDisplay = row.stateDisplay;
    if (row.premium !== previousRow.premium) patch.premium = row.premium;
    if (Object.keys(patch).length > 0) rowDelta[row.statePremiumId] = patch;
  });
  if (Object.keys(rowDelta).length > 0) delta.employerStatePremiums = rowDelta;

  return delta;
}

export function applyPolicyInformationDraftDelta(
  snapshot: PolicyEditDraft,
  delta: PolicyInformationDraftDelta | null | undefined
): PolicyEditDraft {
  if (!delta) {
    return snapshot;
  }

  const { employerStatePremiums: rowDelta, ...scalarDelta } = delta;
  const merged: PolicyEditDraft = { ...snapshot, ...scalarDelta };
  if (rowDelta) {
    merged.employerStatePremiums = (snapshot.employerStatePremiums ?? []).map((row) =>
      rowDelta[row.statePremiumId] ? { ...row, ...rowDelta[row.statePremiumId] } : row
    );
  }

  return merged;
}

function normalizeSummaryValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return EMPTY_SUMMARY_VALUE;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : EMPTY_SUMMARY_VALUE;
}

function addDelta(
  deltas: DraftDelta[],
  label: string,
  previousValue: string | number | undefined,
  newValue: string | number | undefined
): void {
  const previous = normalizeSummaryValue(previousValue);
  const next = normalizeSummaryValue(newValue);

  if (previous === next) {
    return;
  }

  deltas.push({
    label,
    previousValue: previous,
    newValue: next,
  });
}

export function buildDraftDeltas(snapshot: PolicyEditDraft, draft: PolicyEditDraft): DraftDelta[] {
  const deltas: DraftDelta[] = [];

  addDelta(deltas, 'Interstate risk number', snapshot.interstateRiskNumber, draft.interstateRiskNumber);
  addDelta(deltas, 'Legal nature code', snapshot.legalNatureCode, draft.legalNatureCode);
  addDelta(deltas, 'Retro rating code', snapshot.retroRatingCode, draft.retroRatingCode);
  addDelta(deltas, 'Minimum premium state code', snapshot.minimumPremiumStateCode, draft.minimumPremiumStateCode);
  addDelta(deltas, 'Plan type code', snapshot.planTypeCode, draft.planTypeCode);
  addDelta(deltas, 'Producer name', snapshot.producerName, draft.producerName);
  addDelta(deltas, 'Policy type code', snapshot.policyTypeCode, draft.policyTypeCode);
  addDelta(deltas, 'Policy effective date', snapshot.policyEffectiveDate, draft.policyEffectiveDate);
  addDelta(deltas, 'Policy expiration date', snapshot.policyExpirationDate, draft.policyExpirationDate);
  addDelta(deltas, 'Policy issue date', snapshot.policyIssueDate, draft.policyIssueDate);
  addDelta(deltas, 'Bodily injury accident limit', snapshot.bodilyInjuryAccidentLimit, draft.bodilyInjuryAccidentLimit);
  addDelta(deltas, 'Bodily injury disease policy limit', snapshot.bodilyInjuryDiseasePolicyLimit, draft.bodilyInjuryDiseasePolicyLimit);
  addDelta(deltas, 'Bodily injury disease employee limit', snapshot.bodilyInjuryDiseaseEmployeeLimit, draft.bodilyInjuryDiseaseEmployeeLimit);

  draft.employerStatePremiums?.forEach((row) => {
    const previousRow = snapshot.employerStatePremiums?.find(
      (candidate) => candidate.statePremiumId === row.statePremiumId
    );
    if (!previousRow) {
      return;
    }

    addDelta(deltas, 'Employer state', previousRow.stateDisplay, row.stateDisplay);
    addDelta(deltas, 'Employer state premium', previousRow.premium, row.premium);
  });

  return deltas;
}

function findCodeDescription(lookups: CodeDescriptionLookupOption[] | undefined, codeValue: string): string {
  if (!lookups || codeValue === EMPTY_SUMMARY_VALUE) {
    return codeValue;
  }

  const match = lookups.find((item) => item.code === codeValue);
  return match?.description ?? codeValue;
}

function findStateDisplayValue(
  stateLookups: StateLookupOption[] | undefined,
  stateCode: string,
  fallbackDisplay?: string
): string {
  if (stateCode === EMPTY_SUMMARY_VALUE) {
    return stateCode;
  }

  const numericStateCode = Number(stateCode);
  if (Number.isFinite(numericStateCode) && stateLookups) {
    const match = stateLookups.find((item) => item.stateCode === numericStateCode);
    if (match) {
      return `${match.stateAbbreviation} - ${match.stateName}`;
    }
  }

  return fallbackDisplay || stateCode;
}

function formatCurrencySummaryValue(rawValue: string): string {
  if (rawValue === EMPTY_SUMMARY_VALUE) {
    return rawValue;
  }

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) {
    return rawValue;
  }

  return CURRENCY_FORMATTER.format(numericValue);
}

function shouldFormatAsCurrency(label: string): boolean {
  return (
    label === 'Bodily injury accident limit' ||
    label === 'Bodily injury disease policy limit' ||
    label === 'Bodily injury disease employee limit' ||
    label === 'Employer state premium'
  );
}

function getDisplayLabel(label: string): string {
  return CODE_FIELD_DISPLAY_LABELS[label] ?? label;
}

function formatDisplaySummaryValue(
  label: string,
  rawValue: string,
  options: ChangedFieldSummaryDisplayOptions
): string {
  if (shouldFormatAsCurrency(label)) {
    return formatCurrencySummaryValue(rawValue);
  }

  if (label === 'Legal nature code') {
    return findCodeDescription(options.legalNatureLookups, rawValue);
  }

  if (label === 'Retro rating code') {
    return findCodeDescription(options.retroRatingLookups, rawValue);
  }

  if (label === 'Policy type code') {
    return findCodeDescription(options.policyTypeLookups, rawValue);
  }

  if (label === 'Plan type code') {
    const fromPlanTypes = findCodeDescription(options.policyPlanTypeLookups, rawValue);
    if (fromPlanTypes !== rawValue) {
      return fromPlanTypes;
    }

    return findCodeDescription(options.marketLookups, rawValue);
  }

  if (label === 'Minimum premium state code') {
    return findStateDisplayValue(options.stateLookups, rawValue);
  }

  return rawValue;
}

export function buildChangedFieldSummaryLines(
  snapshot: PolicyEditDraft,
  draft: PolicyEditDraft,
  options: ChangedFieldSummaryDisplayOptions = {}
): string[] {
  return buildDraftDeltas(snapshot, draft).map((delta) => {
    const displayLabel = getDisplayLabel(delta.label);
    const previousValue = formatDisplaySummaryValue(delta.label, delta.previousValue, options);
    const newValue = formatDisplaySummaryValue(delta.label, delta.newValue, options);

    return `${displayLabel} changed from ${previousValue} to ${newValue}`;
  });
}

export function hasDraftChanges(snapshot: PolicyEditDraft, draft: PolicyEditDraft): boolean {
  return buildDraftDeltas(snapshot, draft).length > 0 || (snapshot.employerStatePremiums ?? []).some((row, index) => {
    const next = draft.employerStatePremiums?.[index];
    return next && (row.stateCode !== next.stateCode || row.premium !== next.premium);
  });
}

export function validateDraft(draft: PolicyEditDraft): PolicyFieldErrors {
  const errors: PolicyFieldErrors = {};

  if (draft.interstateRiskNumber && !/^\d{9}$/.test(draft.interstateRiskNumber.trim()))
    errors.interstateRiskNumber = 'Interstate risk ID must be exactly 9 digits when provided.';

  validateRequiredFields(draft, errors);
  validateDates(draft, errors);
  validateLiabilityLimits(draft, errors);
  validateEmployerStatePremiums(draft, errors);

  return errors;
}

export function buildPolicySectionPayload(draft: PolicyEditDraft): UpdatePolicyRequest['policy'] {
  return {
    interstateRiskNumber: draft.interstateRiskNumber.trim(),
    legalNatureCode: draft.legalNatureCode,
    legalNatureOther: draft.legalNatureOther.trim() || undefined,
    retroRatingCode: draft.retroRatingCode,
    minimumPremiumStateCode: draft.minimumPremiumStateCode,
    planTypeCode: draft.planTypeCode,
    producerName: draft.producerName.trim(),
    policyTypeCode: draft.policyTypeCode,
    policyEffectiveDate: draft.policyEffectiveDate,
    policyExpirationDate: draft.policyExpirationDate,
    policyIssueDate: draft.policyIssueDate,
    bodilyInjuryAccidentLimit: Number(draft.bodilyInjuryAccidentLimit),
    bodilyInjuryDiseasePolicyLimit: Number(draft.bodilyInjuryDiseasePolicyLimit),
    bodilyInjuryDiseaseEmployeeLimit: Number(draft.bodilyInjuryDiseaseEmployeeLimit),
  };
}

export function buildStatePremiumsPayload(
  snapshot: PolicyEditDraft,
  draft: PolicyEditDraft
): UpdatePolicyStatePremiumUnifiedRequest[] {
  // A primary row's stateCode is locked in the UI and is never user-controlled;
  // rowKey encodes primary/other classification from the read-time computation.
  const isPrimaryRow = (row: EditableStateRow): boolean => row.rowKey.startsWith('primary-');

  return (draft.employerStatePremiums ?? [])
    .filter((row) => {
      const previousRow = (snapshot.employerStatePremiums ?? []).find(
        (candidate) => candidate.statePremiumId === row.statePremiumId
      );
      if (!previousRow) return false;
      const stateChanged = !isPrimaryRow(row) && previousRow.stateCode !== row.stateCode;
      return stateChanged || previousRow.premium !== row.premium;
    })
    .map((row) => ({
      statePremiumId: row.statePremiumId,
      ...(isPrimaryRow(row) ? {} : { stateCode: row.stateCode }),
      estimatedStateStandardPremiumTotal: Number(row.premium),
    }));
}
